import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BaseGameProps, GameStatus } from "../common/types";

// Türkçe kelimeler
const TURKISH_WORDS = [
  "armut",
  "elma",
  "muz",
  "çilek",
  "üzüm",
  "kiraz",
  "karpuz",
  "portakal",
  "mandalina",
  "nar",
  "kuş",
  "kedi",
  "köpek",
  "tavşan",
  "aslan",
  "kaplan",
  "fil",
  "zürafa",
  "maymun",
  "timsah",
  "gökyüzü",
  "deniz",
  "orman",
  "dağ",
  "nehir",
  "göl",
  "ada",
  "plaj",
  "çöl",
  "vadi",
  "kitap",
  "kalem",
  "defter",
  "silgi",
  "çanta",
  "masa",
  "sandalye",
  "kapı",
  "pencere",
  "tavan",
  "gitar",
  "piyano",
  "keman",
  "davul",
  "flüt",
  "trompet",
  "saz",
  "ud",
  "tambur",
  "ney",
  "kelebek",
  "asistan",
  "televizyon",
  "bilgisayar",
  "kütüphane",
  "üniversite",
  "kulaklık",
];

export function WordGuess({
  className = "",
  difficulty = "medium",
  onGameEnd,
}: BaseGameProps) {
  const { t } = useTranslation();
  const [word, setWord] = useState<string>("");
  const [guessedLetters, setGuessedLetters] = useState<Set<string>>(new Set());
  const [incorrectGuesses, setIncorrectGuesses] = useState<number>(0);
  const [gameStatus, setGameStatus] = useState<GameStatus>("playing");

  // Zorluk seviyesine göre maksimum hata sayısı
  const getMaxIncorrectGuesses = () => {
    switch (difficulty) {
      case "easy":
        return 8;
      case "medium":
        return 6;
      case "hard":
        return 4;
      default:
        return 6;
    }
  };

  // Zorluk seviyesine göre kelime uzunluğu
  const getWordLengthRange = () => {
    switch (difficulty) {
      case "easy":
        return { min: 3, max: 5 };
      case "medium":
        return { min: 5, max: 7 };
      case "hard":
        return { min: 7, max: 10 };
      default:
        return { min: 5, max: 7 };
    }
  };

  // Türkçe klavye karakterleri
  const KEYBOARD_ROWS = [
    ["q", "w", "e", "r", "t", "y", "u", "ı", "o", "p", "ğ", "ü"],
    ["a", "s", "d", "f", "g", "h", "j", "k", "l", "ş", "i"],
    ["z", "x", "c", "v", "b", "n", "m", "ö", "ç"],
  ];

  // Rastgele kelime seç
  const selectRandomWord = () => {
    const { min, max } = getWordLengthRange();

    // Zorluk seviyesine uygun kelimeleri filtrele
    const filteredWords = TURKISH_WORDS.filter(
      (word) => word.length >= min && word.length <= max
    );
    const pool = filteredWords.length > 0 ? filteredWords : TURKISH_WORDS;
    return pool[Math.floor(Math.random() * pool.length)] ?? "elma";
  };

  // Oyunu başlat
  const initializeGame = () => {
    setWord(selectRandomWord());
    setGuessedLetters(new Set());
    setIncorrectGuesses(0);
    setGameStatus("playing");
  };

  // Harfi tahmin et
  const guessLetter = (letter: string) => {
    if (gameStatus !== "playing") return;

    // Zaten tahmin edilmiş harfse işlem yapma
    if (guessedLetters.has(letter)) return;

    // Yeni tahmin edilen harfleri güncelle
    const newGuessedLetters = new Set(guessedLetters);
    newGuessedLetters.add(letter);
    setGuessedLetters(newGuessedLetters);

    // Kelimede harf yoksa yanlış tahmin sayısını artır
    if (!word.includes(letter)) {
      const newIncorrectGuesses = incorrectGuesses + 1;
      setIncorrectGuesses(newIncorrectGuesses);

      // Maksimum hata sayısına ulaşıldı mı?
      if (newIncorrectGuesses >= getMaxIncorrectGuesses()) {
        setGameStatus("lose");
        if (onGameEnd) onGameEnd("lose");
      }
    }

    // Tüm harfler doğru tahmin edildi mi?
    const allLettersGuessed = [...word].every((char) =>
      newGuessedLetters.has(char)
    );
    if (allLettersGuessed) {
      setGameStatus("win");
      if (onGameEnd) onGameEnd("win");
    }
  };

  // Klavyeden harf girişini dinle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameStatus !== "playing") return;

      const key = e.key.toLowerCase();
      // Sadece Türkçe alfabedeki harfleri kabul et
      if (/^[a-zçğıöşü]$/.test(key)) {
        guessLetter(key);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameStatus, guessedLetters, word]);

  // Oyunu başlat
  useEffect(() => {
    initializeGame();
  }, [difficulty]);

  // Gösterilecek kelimeyi oluştur
  const displayWord = [...word]
    .map((letter) => (guessedLetters.has(letter) ? letter : "_"))
    .join(" ");

  // Adam asmaca çizimi
  const renderHangman = () => {
    const maxGuesses = getMaxIncorrectGuesses();
    const step = Math.ceil(maxGuesses / 6);
    return (
      <div className="mx-auto mb-2 flex h-28 w-24 items-center justify-center sm:mb-4 sm:h-40 sm:w-32">
        <svg viewBox="0 0 120 160" width="100%" height="100%" className="block">
          {/* Darağacı */}
          <rect x="10" y="10" width="8" height="120" fill="#374151" />{" "}
          {/* Dikey direk */}
          <rect x="10" y="10" width="60" height="8" fill="#374151" />{" "}
          {/* Üst yatay */}
          <rect x="68" y="10" width="8" height="24" fill="#374151" />{" "}
          {/* Askı */}
          {/* Baş */}
          {incorrectGuesses >= 1 * step && (
            <circle
              cx="72"
              cy="44"
              r="16"
              stroke="#374151"
              strokeWidth="4"
              fill="none"
            />
          )}
          {/* Gövde */}
          {incorrectGuesses >= 2 * step && (
            <line
              x1="72"
              y1="60"
              x2="72"
              y2="100"
              stroke="#374151"
              strokeWidth="4"
            />
          )}
          {/* Sol kol */}
          {incorrectGuesses >= 3 * step && (
            <line
              x1="72"
              y1="70"
              x2="52"
              y2="90"
              stroke="#374151"
              strokeWidth="4"
            />
          )}
          {/* Sağ kol */}
          {incorrectGuesses >= 4 * step && (
            <line
              x1="72"
              y1="70"
              x2="92"
              y2="90"
              stroke="#374151"
              strokeWidth="4"
            />
          )}
          {/* Sol bacak */}
          {incorrectGuesses >= 5 * step && (
            <line
              x1="72"
              y1="100"
              x2="58"
              y2="130"
              stroke="#374151"
              strokeWidth="4"
            />
          )}
          {/* Sağ bacak */}
          {incorrectGuesses >= 6 * step && (
            <line
              x1="72"
              y1="100"
              x2="86"
              y2="130"
              stroke="#374151"
              strokeWidth="4"
            />
          )}
        </svg>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col items-center overflow-y-auto px-3 py-2 sm:px-4",
        className
      )}
    >
      <div className="mb-1 hidden text-sm sm:block">
        <span className="font-medium">{t("games.ticTacToe.difficultyLabel")}:</span>{" "}
        {t(`games.difficulty.${difficulty}`)}
      </div>

      <div className="mb-1 text-sm">
        <span className="font-medium">{t("games.wordGuess.remaining")}:</span>{" "}
        {getMaxIncorrectGuesses() - incorrectGuesses}
      </div>

      {/* Adam asmaca çizimi */}
      {renderHangman()}

      {/* Kelime gösterimi */}
      <div className="my-3 break-all text-center font-mono text-xl tracking-[0.2em] sm:my-4 sm:text-3xl sm:tracking-widest">
        {gameStatus === "lose" ? word : displayWord}
      </div>

      {gameStatus === "win" && (
        <div className="mb-3 font-bold text-green-500 sm:mb-4">
          {t("games.wordGuess.win")}
        </div>
      )}

      {gameStatus === "lose" && (
        <div className="mb-3 font-bold text-red-500 sm:mb-4">
          {t("games.wordGuess.lose")}
        </div>
      )}

      <div className="my-2 w-full max-w-md">
        {KEYBOARD_ROWS.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="mb-1 flex justify-center gap-1"
          >
            {row.map((letter) => (
              <button
                key={letter}
                className={cn(
                  "flex h-9 min-w-0 flex-1 items-center justify-center rounded-md text-sm font-medium sm:h-8 sm:max-w-8 sm:flex-none sm:w-8",
                  guessedLetters.has(letter) && word.includes(letter)
                    ? "bg-green-500 text-white"
                    : guessedLetters.has(letter)
                    ? "bg-red-500 text-white"
                    : "bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                )}
                onClick={() => guessLetter(letter)}
                disabled={
                  guessedLetters.has(letter) || gameStatus !== "playing"
                }
              >
                {letter}
              </button>
            ))}
          </div>
        ))}
      </div>

      {gameStatus !== "playing" && (
        <Button onClick={initializeGame} className="mt-3 sm:mt-4">
          {t("games.ticTacToe.restart")}
        </Button>
      )}
    </div>
  );
}
